import type { ReactNode } from 'react';

import type { DataExplorerTreeProps } from './types';
import { cn } from './utils';

function defaultGetChildren<TNode>(node: TNode): TNode[] | undefined {
  if (!node || typeof node !== 'object') {
    return undefined;
  }
  const candidate = (node as { children?: unknown }).children;
  return Array.isArray(candidate) ? (candidate as TNode[]) : undefined;
}

export function DataExplorerTree<TNode = unknown, TContext = unknown>({
  nodes = [],
  context,
  capabilities,
  className,
  classNames,
  empty,
  selectedNodeId,
  getNodeId,
  getChildren = defaultGetChildren,
  renderNode,
  onSelect,
}: DataExplorerTreeProps<TNode, TContext>) {
  const resolvedNodes = Array.isArray(nodes) ? nodes : [];

  const renderTreeNodes = (currentNodes: TNode[], depth: number): ReactNode =>
    currentNodes.map((node, index) => {
      const nodeId = getNodeId ? getNodeId(node, index) : `${depth}-${index}`;
      const childNodes = getChildren(node, index, depth) ?? [];
      const hasChildren = childNodes.length > 0;
      const selected = String(selectedNodeId ?? '') === String(nodeId);
      const renderedChildren = hasChildren ? renderTreeNodes(childNodes, depth + 1) : null;

      if (!renderNode) {
        return renderedChildren;
      }

      return (
        <div key={nodeId} className="min-w-0">
          {renderNode({
            node,
            index,
            depth,
            context,
            capabilities,
            childNodes,
            hasChildren,
            selected,
            onSelect,
            renderChildren: () => renderedChildren,
          })}
        </div>
      );
    });

  if (resolvedNodes.length === 0) {
    if (!empty) {
      return null;
    }
    return <div className={cn(classNames?.empty)}>{empty}</div>;
  }

  return (
    <div className={cn('min-w-0', classNames?.root, className)}>
      {renderTreeNodes(resolvedNodes, 0)}
    </div>
  );
}
